import React from "react";
import { motion } from "framer-motion";
import { 
  Users, 
  Shield, 
  Clock, 
  Target, 
  Award, 
  Lightbulb, 
  Rocket,
  TrendingUp,
  HeartHandshake,
  CheckCircle2
} from "lucide-react";

const About = () => {
  const easePremium = [0.22, 1, 0.36, 1];

  const stats = [
    { icon: Users, label: "Clientes activos", value: "150+" },
    { icon: Shield, label: "Proyectos entregados", value: "300+" },
    { icon: Clock, label: "Años de experiencia", value: "10+" },
    { icon: TrendingUp, label: "Crecimiento anual", value: "99%" },
  ];

  const values = [
    {
      icon: Lightbulb,
      title: "Innovación",
      text: "Implementamos tecnologías actuales para mantener a nuestros clientes competitivos."
    },
    {
      icon: Target,
      title: "Compromiso",
      text: "Cumplimos plazos y superamos expectativas en cada proyecto que emprendemos."
    },
    {
      icon: Award,
      title: "Calidad",
      text: "Software robusto, escalable y mantenible que cumple estándares enterprise."
    },
    {
      icon: HeartHandshake,
      title: "Confianza",
      text: "Relaciones duraderas basadas en transparencia y resultados tangibles."
    },
    {
      icon: Rocket,
      title: "Crecimiento",
      text: "Soluciones que impulsan el crecimiento sostenible de tu negocio."
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1, ease: easePremium }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easePremium } }
  };

  return (
    <section id="nosotros" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      
      {/* Fondo sutil con patrón de puntos */}
      <div 
        className="absolute inset-0 z-0 opacity-[0.02]"
        style={{
          backgroundImage: 'radial-gradient(#059669 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        
        {/* ========================================== */}
        {/* ENCABEZADO                                  */}
        {/* ========================================== */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-20"
        >
          <motion.span
            variants={itemVariants}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 text-sm font-semibold text-emerald-700 mb-6"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            Sobre Visioner7
          </motion.span>

          <motion.h2 
            variants={itemVariants}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight"
          >
            Software que impulsa <br className="hidden md:block" />
            <span className="text-emerald-600">tu crecimiento</span>
          </motion.h2>

          <motion.p 
            variants={itemVariants}
            className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed"
          >
            En Visioner7 transformamos procesos complejos en soluciones digitales simples. 
            Combinamos experiencia técnica con visión estratégica para que tu empresa 
            opere con máxima eficiencia.
          </motion.p>
        </motion.div>

        {/* ========================================== */}
        {/* CONTENIDO PRINCIPAL: Texto + Imagen        */}
        {/* ========================================== */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24"
        >
          {/* Imagen */}
          <motion.div variants={itemVariants} className="relative order-2 lg:order-1">
            <div className="relative bg-slate-50 rounded-2xl p-8 border border-slate-100">
              <img
                src="/images/logo22.png"
                alt="Visioner7"
                className="w-full max-w-md mx-auto"
              />
            </div>
            
            {/* Tarjeta flotante con dato clave */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              viewport={{ once: true }}
              className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg border border-slate-200 p-4 hidden md:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Soporte dedicado</div>
                  <div className="text-xs text-slate-500">Respuesta en menos de 1 hora</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Texto */}
          <motion.div variants={itemVariants} className="space-y-6 order-1 lg:order-2">
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 tracking-tight">
                Tu socio tecnológico <br className="hidden md:block" />
                <span className="text-emerald-600">de confianza</span>
              </h3>
              
              <div className="space-y-4">
                <p className="text-slate-700 text-lg leading-relaxed">
                  Somos especialistas en <strong className="text-slate-900">desarrollo de software a medida</strong>, 
                  sistemas de facturación electrónica, gestión contable y aplicaciones móviles 
                  para PYMES, comercios y profesionales.
                </p>
                
                <p className="text-slate-700 text-lg leading-relaxed">
                  Nuestro enfoque se basa en entender profundamente las necesidades de cada cliente 
                  para entregar soluciones que no solo resuelven problemas inmediatos, sino que 
                  también <strong className="text-slate-900">preparan tu negocio para el futuro</strong>.
                </p>

                <p className="text-slate-700 text-lg leading-relaxed">
                  Creemos en la tecnología como un habilitador de crecimiento, innovación 
                  y competitividad sostenible.
                </p>
              </div>
            </div>

            {/* Mini stats inline */}
            <div className="grid grid-cols-2 gap-4 pt-6">
              <div className="p-5 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-3xl font-bold text-emerald-600 mb-1">100%</div>
                <div className="text-sm font-medium text-slate-600">Clientes satisfechos</div>
              </div>
              <div className="p-5 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-3xl font-bold text-emerald-600 mb-1">24/7</div>
                <div className="text-sm font-medium text-slate-600">Soporte continuo</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* ========================================== */}
        {/* MISIÓN & VISIÓN                            */}
        {/* ========================================== */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-2 gap-6 mb-24"
        >
          {/* Misión */}
          <motion.div
            variants={itemVariants}
            className="group p-8 bg-white rounded-2xl border border-slate-200 hover:border-emerald-200 hover:shadow-lg transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6 group-hover:bg-emerald-100 transition-colors">
              <Target className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Nuestra Misión</h3>
            <p className="text-slate-600 leading-relaxed text-lg">
              Desarrollar soluciones digitales que fortalezcan la competitividad 
              de nuestros clientes, brindando software confiable, seguro y escalable 
              que se adapte a sus necesidades reales.
            </p>
          </motion.div>

          {/* Visión */}
          <motion.div
            variants={itemVariants}
            className="group p-8 bg-white rounded-2xl border border-slate-200 hover:border-emerald-200 hover:shadow-lg transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6 group-hover:bg-emerald-100 transition-colors">
              <Rocket className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Nuestra Visión</h3>
            <p className="text-slate-600 leading-relaxed text-lg">
              Ser el socio tecnológico de referencia para empresas que buscan 
              digitalizar sus operaciones con soluciones inteligentes, confiables 
              y orientadas a resultados medibles.
            </p>
          </motion.div>
        </motion.div>

        {/* ========================================== */}
        {/* VALORES                                    */}
        {/* ========================================== */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-24"
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Nuestros valores
            </h3>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Principios que guían cada decisión y proyecto que emprendemos
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  variants={itemVariants}
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.3 }}
                  className="group p-6 bg-white rounded-xl border border-slate-100 hover:border-emerald-100 hover:shadow-md transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center mb-4 group-hover:bg-emerald-50 group-hover:border-emerald-100 transition-colors">
                    <Icon className="w-5 h-5 text-slate-600 group-hover:text-emerald-600 transition-colors" />
                  </div>
                  <h4 className="text-base font-semibold text-slate-900 mb-2">{value.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{value.text}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* ========================================== */}
        {/* ESTADÍSTICAS                               */}
        {/* ========================================== */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-slate-50 rounded-2xl border border-slate-100 p-8 md:p-12"
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Visioner7 en cifras
            </h3>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Resultados que reflejan nuestro compromiso con la excelencia
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  variants={itemVariants}
                  className="text-center p-6 bg-white rounded-xl border border-slate-200"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">
                    {stat.value}
                  </div>
                  <p className="text-sm font-medium text-slate-600">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default About;